import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Profile.css';

// Mock user data
const mockUser = {
  name: 'M',
  email: 'mrlcreation6@gmail.com',
  version: 'v1.2.20'
};

interface MenuItemProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  rightElement?: React.ReactNode;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(true);

  const MenuItem: React.FC<MenuItemProps> = ({ icon, title, subtitle, onClick, rightElement }) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && onClick && !rightElement) {
        e.preventDefault();
        onClick();
      }
    };

    return (
      <div
        className="profile-menu-item"
        onClick={onClick}
        role={!rightElement ? "button" : undefined}
        tabIndex={!rightElement ? 0 : undefined}
        onKeyDown={handleKeyDown}
      >
        <div className="profile-menu-icon">
          {icon}
        </div>
        <div className="profile-menu-content">
          <div className="profile-menu-title">{title}</div>
          {subtitle && <div className="profile-menu-subtitle">{subtitle}</div>}
        </div>
        {rightElement || (
          <svg className="profile-menu-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    );
  };

  const Toggle: React.FC<{ enabled: boolean; onChange: () => void }> = ({ enabled, onChange }) => (
    <button 
      className={`profile-toggle ${enabled ? 'enabled' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      aria-label="Toggle"
    >
      <div className="profile-toggle-handle" />
    </button>
  );

  return (
    <div className="profile-container">
      {/* Profile Header Card */}
      <div className="profile-header-card">
        <div className="profile-avatar-wrapper">
          <div className="profile-avatar-gradient" />
          <div className="profile-avatar">
            <span className="profile-avatar-text">{mockUser.name}</span>
          </div>
        </div>
        <div className="profile-email-badge">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2.66669 2.66669H13.3334C14.0667 2.66669 14.6667 3.26669 14.6667 4.00002V12C14.6667 12.7334 14.0667 13.3334 13.3334 13.3334H2.66669C1.93335 13.3334 1.33335 12.7334 1.33335 12V4.00002C1.33335 3.26669 1.93335 2.66669 2.66669 2.66669Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14.6667 4L8.00002 8.66667L1.33335 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="profile-email-text">{mockUser.email}</span>
        </div>
      </div>

      {/* Main Menu Card */}
      <div className="profile-menu-card">
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" fill="currentColor"/>
              <path d="M10 12.5C4.47715 12.5 0 14.7386 0 17.5V20H20V17.5C20 14.7386 15.5228 12.5 10 12.5Z" fill="currentColor"/>
            </svg>
          }
          title="Registration Info"
          subtitle={mockUser.email}
          onClick={() => navigate('/profile-info')}
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.83331 9.16667V5.83333C5.83331 4.72826 6.27229 3.66846 7.05369 2.88706C7.83509 2.10565 8.89489 1.66667 9.99998 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1666 4.72826 14.1666 5.83333V9.16667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Security"
          onClick={() => navigate('/profile-security')}
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13.3333 5.83333L16.6667 9.16667L13.3333 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.16665 9.16667H16.6666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.16665 16.6667H4.16665C3.94563 16.6667 3.73367 16.5789 3.57739 16.4226C3.42111 16.2663 3.33331 16.0544 3.33331 15.8333V3.33333C3.33331 3.11232 3.42111 2.90036 3.57739 2.74408C3.73367 2.5878 3.94563 2.5 4.16665 2.5H9.16665" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Referrals"
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 6.66667C15 5.34058 14.4732 4.06881 13.5355 3.13113C12.5979 2.19345 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.19345 6.46447 3.13113C5.52678 4.06881 5 5.34058 5 6.66667C5 12.5 2.5 14.1667 2.5 14.1667H17.5C17.5 14.1667 15 12.5 15 6.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11.4417 17.5C11.2952 17.7526 11.085 17.9622 10.8319 18.108C10.5787 18.2537 10.292 18.3304 10 18.3304C9.70802 18.3304 9.42132 18.2537 9.16815 18.108C8.91499 17.9622 8.70484 17.7526 8.55835 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Notifications"
          rightElement={<Toggle enabled={notificationsEnabled} onChange={() => setNotificationsEnabled(!notificationsEnabled)} />}
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3.33331 6.66667H16.6666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8.33331 10H11.6666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 16.6667H5C4.55797 16.6667 4.13405 16.4911 3.82149 16.1785C3.50893 15.866 3.33333 15.442 3.33333 15V5C3.33333 4.55797 3.50893 4.13405 3.82149 3.82149C4.13405 3.50893 4.55797 3.33333 5 3.33333H15C15.442 3.33333 15.866 3.50893 16.1785 3.82149C16.4911 4.13405 16.6667 4.55797 16.6667 5V15C16.6667 15.442 16.4911 15.866 16.1785 16.1785C15.866 16.4911 15.442 16.6667 15 16.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="P2P Payment Methods"
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2.5 7.5L10 1.66667L17.5 7.5V16.6667C17.5 17.1087 17.3244 17.5326 17.0118 17.8452C16.6993 18.1577 16.2754 18.3333 15.8333 18.3333H4.16667C3.72464 18.3333 3.30072 18.1577 2.98816 17.8452C2.67559 17.5326 2.5 17.1087 2.5 16.6667V7.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 18.3333V10H12.5V18.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Merchant Dashboard"
          onClick={() => navigate('/p2p-merchant-dashboard')}
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 1.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 16.6667V18.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51666 3.51666L4.69166 4.69166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.3083 15.3083L16.4833 16.4833" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1.66666 10H3.33332" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.6667 10H18.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51666 16.4833L4.69166 15.3083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.3083 4.69166L16.4833 3.51666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Enable Dark Mode"
          rightElement={<Toggle enabled={darkModeEnabled} onChange={() => setDarkModeEnabled(!darkModeEnabled)} />}
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 9.16667C11.3807 9.16667 12.5 8.04738 12.5 6.66667C12.5 5.28596 11.3807 4.16667 10 4.16667C8.61929 4.16667 7.5 5.28596 7.5 6.66667C7.5 8.04738 8.61929 9.16667 10 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.83334 15.8333V14.1667C5.83334 13.2826 6.1845 12.4348 6.80962 11.8097C7.43475 11.1846 8.28262 10.8333 9.16668 10.8333H10.8333C11.7174 10.8333 12.5652 11.1846 13.1904 11.8097C13.8155 12.4348 14.1667 13.2826 14.1667 14.1667V15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Disable 2FA"
        />
        <div className="profile-menu-divider" />
        
        <MenuItem
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 1.66667L2.5 5.83333L10 10L17.5 5.83333L10 1.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 14.1667L10 18.3333L17.5 14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 10L10 14.1667L17.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title="Developer Options"
        />
      </div>

      {/* Logout Card */}
      <div className="profile-logout-card">
        <button className="profile-logout-button">
          <div className="profile-menu-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.3333 14.1667L17.5 10L13.3333 5.83333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 10H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="profile-logout-text">Logout</span>
          <svg className="profile-menu-arrow profile-logout-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Version Text */}
      <div className="profile-version">{mockUser.version}</div>
    </div>
  );
};

export default Profile;